import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const MiSuscripcion = () => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paginaCobertura, setPaginaCobertura] = useState(1);
  const [paginaPagos, setPaginaPagos] = useState(1);

  const colores = {
    fondo:
      "linear-gradient(135deg, #0e1319 0%, #212529 40%, #0d6efd 100%)",
    azul: "#0d6efd",
    verde: "#198754",
    amarillo: "#ffc107",
    rojo: "#dc3545",
    gris: "#6c757d",
  };

  const token = useMemo(
    () =>
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "",
    []
  );

  const formatearFecha = (fecha) => {
    if (!fecha) return "No registrado";

    const valor = new Date(fecha);

    if (Number.isNaN(valor.getTime())) {
      return "No registrado";
    }

    return valor.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatearMoneda = (valor, moneda = "COP") => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: moneda || "COP",
      maximumFractionDigits: 0,
    }).format(Number(valor || 0));
  };

  const textoEstado = (estado) =>
    String(estado || "SIN_SUSCRIPCION")
      .replaceAll("_", " ")
      .trim();

  const obtenerEstiloEstado = (estado) => {
    const valor = String(estado || "")
      .trim()
      .toUpperCase();

    if (
      ["ACTIVA", "POR_VENCER", "GRACIA", "APROBADO"].includes(valor)
    ) {
      return {
        color: colores.verde,
        clase: "bg-success text-white",
        icono: "fa-solid fa-circle-check",
      };
    }

    if (
      ["PROGRAMADA", "PENDIENTE", "PENDIENTE_PAGO"].includes(valor)
    ) {
      return {
        color: colores.amarillo,
        clase: "bg-warning text-dark",
        icono: "fa-regular fa-clock",
      };
    }

    if (
      [
        "VENCIDA",
        "SUSPENDIDA",
        "CANCELADA",
        "RECHAZADO",
        "ANULADO",
      ].includes(valor)
    ) {
      return {
        color: colores.rojo,
        clase: "bg-danger text-white",
        icono: "fa-solid fa-circle-exclamation",
      };
    }

    return {
      color: colores.gris,
      clase: "bg-secondary text-white",
      icono: "fa-solid fa-circle-info",
    };
  };

  const cargarSuscripcion = useCallback(async () => {
    try {
      if (!token) {
        Swal.fire(
          "Sesión no encontrada",
          "Ingrese nuevamente al sistema.",
          "warning"
        );

        navigate("/");
        return;
      }

      setLoading(true);

      const { data: respuesta } = await axios.get(
        "/api/mi-suscripcion/resumen",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!respuesta?.ok) {
        Swal.fire(
          "Información no disponible",
          respuesta?.message ||
          "No se pudo consultar la información de la suscripción.",
          "warning"
        );

        return;
      }

      setData(respuesta);
      setPaginaCobertura(1);
      setPaginaPagos(1);
    } catch (error) {
      console.error(
        "Error cargando Mi Suscripción:",
        error.response?.data || error.message
      );

      if (error.response?.status === 401) {
        Swal.fire(
          "Sesión expirada",
          "Ingrese nuevamente.",
          "warning"
        );

        localStorage.clear();
        sessionStorage.clear();
        navigate("/");
        return;
      }

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudo cargar la información de la suscripción.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    cargarSuscripcion();
  }, [cargarSuscripcion]);

  const irASeccion = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (loading) {
    return (
      <main className="min-vh-100 d-flex align-items-center justify-content-center bg-body-tertiary">
        <section className="text-center">
          <div className="spinner-border text-dark"></div>

          <p className="mt-3 text-secondary">
            Cargando información de la suscripción...
          </p>
        </section>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="container py-5">
        <section className="alert alert-warning text-center rounded-4">
          No se encontró información de la suscripción.
        </section>

        <div className="text-center">
          <button
            type="button"
            className="btn btn-dark mt-3"
            onClick={() => navigate("/inicio")}
          >
            Volver al inicio
          </button>
        </div>
      </main>
    );
  }

  const estadoPrincipal =
    data?.acceso?.estado || "SIN_SUSCRIPCION";

  const tieneAcceso = Boolean(
    data?.acceso?.tieneAcceso
  );

  const estiloEstadoPrincipal =
    obtenerEstiloEstado(estadoPrincipal);

  const plan = data?.resumen?.planActual || null;
  const periodos = data?.periodos || [];
  const pagos = data?.pagos || [];

  const pagosAprobados = pagos.filter(
    (pago) =>
      String(pago.estadoPago || "")
        .trim()
        .toUpperCase() === "APROBADO"
  ).length;

  const pagosPendientes = pagos.filter(
    (pago) =>
      String(pago.estadoPago || "")
        .trim()
        .toUpperCase() === "PENDIENTE"
  ).length;


  const PERIODOS_POR_PAGINA = 3;
  const PAGOS_POR_PAGINA = 5;

  const totalPaginasCobertura = Math.max(
    1,
    Math.ceil(periodos.length / PERIODOS_POR_PAGINA)
  );

  const inicioCobertura =
    (paginaCobertura - 1) * PERIODOS_POR_PAGINA;

  const periodosPaginados = periodos.slice(
    inicioCobertura,
    inicioCobertura + PERIODOS_POR_PAGINA
  );

  const paginaAnteriorCobertura = () => {
    setPaginaCobertura((paginaActual) =>
      Math.max(1, paginaActual - 1)
    );
  };

  const paginaSiguienteCobertura = () => {
    setPaginaCobertura((paginaActual) =>
      Math.min(
        totalPaginasCobertura,
        paginaActual + 1
      )
    );
  };

  const totalPaginasPagos = Math.max(
    1,
    Math.ceil(pagos.length / PAGOS_POR_PAGINA)
  );

  const inicioPagos =
    (paginaPagos - 1) * PAGOS_POR_PAGINA;

  const pagosPaginados = pagos.slice(
    inicioPagos,
    inicioPagos + PAGOS_POR_PAGINA
  );

  const paginaAnteriorPagos = () => {
    setPaginaPagos((paginaActual) =>
      Math.max(1, paginaActual - 1)
    );
  };

  const paginaSiguientePagos = () => {
    setPaginaPagos((paginaActual) =>
      Math.min(
        totalPaginasPagos,
        paginaActual + 1
      )
    );
  };

  return (
    <main className="container-fluid min-vh-90">
      <section
        className="rounded-4 shadow-sm p-4 mb-3 text-white"
        style={{ background: colores.fondo }}
      >
        <div className="row align-items-center text-center text-lg-start">
          <div className="col-lg-5">
            <div className="small text-uppercase opacity-75 fw-semibold mb-1">
              Cuenta y facturación
            </div>

            <h2 className="fw-bold mb-1">
              Mi Suscripción
            </h2>

            <p className="mb-2 opacity-75">
              Consulta tu estado, cobertura y pagos.
            </p>

            <span
              className={`badge rounded-pill px-3 py-2 ${estiloEstadoPrincipal.clase}`}
            >
              <i
                className={`${estiloEstadoPrincipal.icono} me-2`}
              ></i>
              {textoEstado(estadoPrincipal)}
            </span>
          </div>

          <div className="col-lg-7 mt-4 mt-lg-0">
            <div className="row g-2 justify-content-center justify-content-lg-end">
              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={cargarSuscripcion}
                >
                  <i className="fa-solid fa-rotate d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Actualizar
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() =>
                    irASeccion("coberturaSuscripcion")
                  }
                >
                  <i className="fa-solid fa-shield-halved d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Cobertura
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() =>
                    irASeccion("historialPagos")
                  }
                >
                  <i className="fa-solid fa-receipt d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Pagos
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() => navigate("/inicio")}
                >
                  <i className="fa-solid fa-house d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Inicio
                  </small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-regular fa-calendar"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Inicio
                </div>

                <div className="fw-bold fs-6">
                  {formatearFecha(
                    data?.resumen?.fechaInicio
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-regular fa-calendar-check"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Cobertura hasta
                </div>

                <div className="fw-bold fs-6">
                  {formatearFecha(
                    data?.resumen?.coberturaHasta
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-solid fa-wallet"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Total pagado
                </div>

                <div className="fw-bold fs-6">
                  {formatearMoneda(
                    data?.resumen?.totalPagado
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-solid fa-arrows-rotate"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Renovaciones
                </div>

                <div className="fw-bold fs-6">
                  {data?.resumen
                    ?.renovacionesAprobadas || 0}
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="row g-4 mb-4">
        <aside className="col-12 col-xl-4">
          <article className="bg-white border rounded-4 shadow-sm overflow-hidden h-100">
            <div className="p-4 text-center border-bottom">
              <div
                className="mx-auto rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                style={{
                  width: 120,
                  height: 120,
                  backgroundColor: estiloEstadoPrincipal.color,
                  border: "5px solid #f8f9fa",
                }}
              >
                <i
                  className={`${estiloEstadoPrincipal.icono} fs-1`}
                ></i>
              </div>

              <h4 className="fw-bold mt-3 mb-2">
                {textoEstado(estadoPrincipal)}
              </h4>

              <span
                className={`badge rounded-pill px-3 py-2 ${tieneAcceso
                  ? "bg-success"
                  : "bg-danger"
                  }`}
              >
                <i
                  className={`fa-solid ${tieneAcceso
                    ? "fa-unlock"
                    : "fa-lock"
                    } me-2`}
                ></i>

                {tieneAcceso
                  ? "Acceso habilitado"
                  : "Acceso restringido"}
              </span>
            </div>

            <div className="p-3 p-md-4">
              <div className="row g-3">

                {/* PLAN ACTUAL */}
                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start gap-2 gap-sm-3">

                      <div
                        className="rounded-circle bg-white border d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 46,
                          height: 46,
                        }}
                      >
                        <i
                          className="fa-solid fa-gem"
                          style={{
                            color: colores.azul,
                          }}
                        ></i>
                      </div>

                      <div className="flex-grow-1">
                        <div className="small text-secondary">
                          Plan actual
                        </div>

                        <div className="fw-bold text-dark">
                          {plan?.nombrePlan ||
                            "Sin plan activo"}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* VALOR DEL PLAN */}
                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start gap-2 gap-sm-3">

                      <div
                        className="rounded-circle bg-white border d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 46,
                          height: 46,
                        }}
                      >
                        <i
                          className="fa-solid fa-dollar-sign"
                          style={{
                            color: colores.azul,
                          }}
                        ></i>
                      </div>

                      <div className="flex-grow-1">
                        <div className="small text-secondary">
                          Valor del plan
                        </div>

                        <div className="fw-bold text-dark">
                          {plan?.valorPlan != null
                            ? formatearMoneda(
                              plan.valorPlan,
                              plan.moneda
                            )
                            : "No registrado"}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* PAGOS APROBADOS */}
                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start gap-2 gap-sm-3">

                      <div
                        className="rounded-circle bg-white border d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 46,
                          height: 46,
                        }}
                      >
                        <i className="fa-solid fa-circle-check text-success"></i>
                      </div>

                      <div className="flex-grow-1">
                        <div className="small text-secondary">
                          Pagos aprobados
                        </div>

                        <div className="fw-bold text-dark">
                          {pagosAprobados}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* PAGOS PENDIENTES */}
                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start gap-2 gap-sm-3">

                      <div
                        className="rounded-circle bg-white border d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 46,
                          height: 46,
                        }}
                      >
                        <i
                          className={`fa-regular fa-clock ${pagosPendientes > 0
                              ? "text-warning"
                              : "text-success"
                            }`}
                        ></i>
                      </div>

                      <div className="flex-grow-1">
                        <div className="small text-secondary">
                          Pagos pendientes
                        </div>

                        <div className="fw-bold text-dark">
                          {pagosPendientes}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </article>
        </aside>

        <section id="coberturaSuscripcion" className="col-12 col-xl-8">
          <article className="bg-white border rounded-4 shadow-sm p-3 p-md-4 h-100">

            {/* ENCABEZADO */}
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 mb-3">
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-md-start gap-2">

                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{ width: 38,height: 38,backgroundColor: colores.azul,}}>
                  <i className="fa-solid fa-layer-group"></i>
                </div>

                <div className="text-center text-md-start">
                  <h5
                    className="fw-bold mb-0"
                    style={{
                      color: colores.azul,
                    }}
                  >
                    Cobertura de servicio
                  </h5>

                  <small className="text-secondary">
                    Períodos contratados de tu suscripción
                  </small>
                </div>

              </div>

              <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                {periodos.length} período
                {periodos.length === 1 ? "" : "s"}
              </span>

            </div>

            <hr />

            {/* SIN PERÍODOS */}
            {periodos.length === 0 ? (
              <div className="text-center py-5">

                <div
                  className="rounded-circle bg-light border d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{
                    width: 70,
                    height: 70,
                  }}
                >
                  <i className="fa-regular fa-calendar-xmark fs-3 text-secondary"></i>
                </div>

                <h5 className="fw-bold">
                  Sin cobertura registrada
                </h5>

                <p className="text-secondary mb-0">
                  No tienes períodos de suscripción registrados.
                </p>

              </div>
            ) : (
              <div className="d-flex flex-column gap-3">

                {periodosPaginados.map((periodo, index) => {
                  const estilo =
                    obtenerEstiloEstado(
                      periodo.estado
                    );

                  return (
                    <div key={periodo._id || index} className="border rounded-4 p-3" >
                      <div className="row align-items-center g-3">

                        {/* ESTADO Y PLAN */}
                        <div className="col-12 col-lg-4">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                              style={{
                                width: 46,
                                height: 46,
                                backgroundColor:
                                  estilo.color,
                              }}
                            >
                              <i className={estilo.icono}></i>
                            </div>

                            <div>
                              <div className="small text-secondary">
                                Período {inicioCobertura + index + 1}
                              </div>
                              <div className="fw-bold">
                                {periodo?.plan
                                  ?.nombrePlan ||
                                  "Suscripción"}
                              </div>
                              <span className={`badge rounded-pill mt-1 ${estilo.clase}`}>
                                {textoEstado(periodo.estado)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* FECHA INICIO */}
                        <div className="col-6 col-md-3 col-lg-2">

                          <div className="small text-secondary">
                            Inicio
                          </div>

                          <div className="fw-semibold">
                            {formatearFecha(
                              periodo.fechaInicio
                            )}
                          </div>

                        </div>

                        {/* FECHA FIN */}
                        <div className="col-6 col-md-3 col-lg-2">

                          <div className="small text-secondary">
                            Fin
                          </div>

                          <div className="fw-semibold">
                            {formatearFecha(
                              periodo.fechaFin
                            )}
                          </div>

                        </div>

                        {/* GRACIA */}
                        <div className="col-6 col-md-3 col-lg-2">

                          <div className="small text-secondary">
                            Gracia
                          </div>

                          <div className="fw-semibold">
                            {periodo.fechaFinGracia
                              ? formatearFecha(
                                periodo.fechaFinGracia
                              )
                              : "No aplica"}
                          </div>

                        </div>

                        {/* ORIGEN */}
                        <div className="col-6 col-md-3 col-lg-2">
                          <div className="small text-secondary">
                            Origen
                          </div>
                          <div className="fw-semibold">
                            {textoEstado(periodo.origen || "—")}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            )}

            {periodos.length > 0 &&
              totalPaginasCobertura > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-dark rounded-3 px-3"
                    onClick={paginaAnteriorCobertura}
                    disabled={paginaCobertura === 1}
                  >
                    <i className="fa-solid fa-arrow-left me-2"></i>
                    Anterior
                  </button>

                  <button
                    type="button"
                    className="btn btn-dark rounded-3 px-3"
                    onClick={paginaSiguienteCobertura}
                    disabled={
                      paginaCobertura ===
                      totalPaginasCobertura
                    }
                  >
                    Siguiente
                    <i className="fa-solid fa-arrow-right ms-2"></i>
                  </button>
                </div>
              )}

          </article>
        </section>
      </section>


      <article
        id="historialPagos"
        className="bg-white border rounded-4 shadow-sm overflow-hidden mb-4"
      >
        <div className="p-3 p-md-4">
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
            <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-center text-md-start">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white"
                style={{
                  width: 38,
                  height: 38,
                  backgroundColor: colores.azul,
                }}
              >
                <i className="fa-solid fa-receipt"></i>
              </div>

              <div>
                <h5
                  className="fw-bold mb-0"
                  style={{ color: colores.azul }}
                >
                  Historial de pagos
                </h5>

                <small className="text-secondary">
                  Suscripciones y renovaciones
                </small>
              </div>
            </div>

            <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
              {pagos.length} pago
              {pagos.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        {pagos.length === 0 ? (
          <div className="text-center py-5 border-top">
            <i className="fa-solid fa-receipt fs-1 text-secondary"></i>

            <h5 className="mt-3">
              Sin pagos registrados
            </h5>

            <p className="text-secondary mb-0">
              Los pagos aparecerán aquí cuando sean
              registrados.
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive border-top">
              <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-4 py-3">
                    Fecha
                  </th>
                  <th className="py-3">
                    Concepto
                  </th>
                  <th className="py-3">
                    Método
                  </th>
                  <th className="py-3">
                    Referencia
                  </th>
                  <th className="py-3">
                    Valor
                  </th>
                  <th className="px-4 py-3">
                    Estado
                  </th>
                </tr>
              </thead>

              <tbody>
                {pagosPaginados.map((pago) => {
                  const estilo =
                    obtenerEstiloEstado(
                      pago.estadoPago
                    );

                  return (
                    <tr key={pago._id}>
                      <td className="px-4 text-nowrap">
                        <i className="fa-regular fa-calendar me-2 text-primary"></i>

                        {formatearFecha(
                          pago.fechaPago ||
                          pago.createdAt
                        )}
                      </td>

                      <td className="fw-semibold">
                        {textoEstado(
                          pago.conceptoPago || "—"
                        )}
                      </td>

                      <td>
                        {textoEstado(
                          pago.metodoPago || "—"
                        )}
                      </td>

                      <td>
                        <span className="text-secondary">
                          {pago.referenciaPago || "—"}
                        </span>
                      </td>

                      <td className="fw-bold text-nowrap">
                        {formatearMoneda(
                          pago.valorPago,
                          pago.moneda
                        )}
                      </td>

                      <td className="px-4">
                        <span
                          className={`badge rounded-pill px-3 py-2 ${estilo.clase}`}
                        >
                          <i
                            className={`${estilo.icono} me-2`}
                          ></i>

                          {textoEstado(
                            pago.estadoPago ||
                            "PENDIENTE"
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>

            {totalPaginasPagos > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 p-md-4 border-top">
                <button
                  type="button"
                  className="btn btn-outline-dark rounded-3 px-3"
                  onClick={paginaAnteriorPagos}
                  disabled={paginaPagos === 1}
                >
                  <i className="fa-solid fa-arrow-left me-2"></i>
                  Anterior
                </button>

                <button
                  type="button"
                  className="btn btn-dark rounded-3 px-3"
                  onClick={paginaSiguientePagos}
                  disabled={
                    paginaPagos ===
                    totalPaginasPagos
                  }
                >
                  Siguiente
                  <i className="fa-solid fa-arrow-right ms-2"></i>
                </button>
              </div>
            )}
          </>
        )}
      </article>
    </main>
  );
};

export default MiSuscripcion;